/**
 * Wörterzeile der Sektion „Anfos & Umsatz": die Fachwörter eines Kapitels in je einem Kurzsatz. Beim ersten Besuch
 * am Desktop offen, am Handy zu, einklappbar; der Zustand gilt je Browser und Kapitel (localStorage nur im try/catch — privates Fenster wirft,
 * dann einfach offen). Ein Erklärtext, keine Daten — darf deshalb zugeklappt werden (P1-V3 E6, P2-V3 Regel Z a).
 *
 * Keine zweite Wahrheit (Regel 7): die Sätze kommen aus `TERMS.*.glossar` bzw. `GLOSSAR`, die WORTLISTE je Kapitel aus
 * `WOERTERZEILE` (kpiDefinitions.ts). Bis 23.10.2026 nahm die P3-Zeile JEDEN TERMS-Eintrag mit `glossar` — neue Sätze
 * für P1/P2 wären still in P3 erschienen (E7.1). Plan 2026-10-23-p1-p2-kpi-review-umbau, Stufe 0.
 */
import { FC, useState } from 'react';
import { Box, Collapse, Link, Typography } from '@mui/material';
import { WOERTERZEILE, wortSatz } from '../../helpers/kpiDefinitions';

export type WoerterKapitel = keyof typeof WOERTERZEILE;

/** Speicherschlüssel je Kapitel; P3 behält seinen Schlüssel von vor dem Umzug (gespeicherter Zustand bleibt gültig). */
export const woerterSpeicher = (kapitel: WoerterKapitel): string =>
  kapitel === 'kanaele' ? 'auswertungen.woerterzeile' : `auswertungen.woerterzeile.${kapitel}`;

/** Sätze eines Kapitels in Listenreihenfolge (Wörter ohne Satz fallen still weg — die Liste prüft der Test). */
export const woerterSaetze = (kapitel: WoerterKapitel): string[] =>
  WOERTERZEILE[kapitel].map(wortSatz).filter((s): s is string => s !== null);

/**
 * Start-Zustand: eine gespeicherte Wahl gewinnt; ohne Wahl am Handy zu, sonst offen (Till 26.10.2026 — am Handy schob
 * die offene Zeile die Empfehlung auf den dritten Bildschirm; Persona-Befund B33).
 */
export const standardOffen = (gespeichert: string | null, schmal: boolean): boolean =>
  gespeichert === 'zu' ? false : gespeichert === 'offen' ? true : !schmal;

/** Schmal = unter dem MUI-Breakpoint `sm` (600 px). */
const SCHMAL_MEDIA = '(max-width: 599.95px)';

const liesOffen = (speicher: string): boolean => {
  let gespeichert: string | null = null;
  try {
    gespeichert = window.localStorage.getItem(speicher);
  } catch {
    /* privates Fenster: keine Wahl gespeichert */
  }
  let schmal = false;
  try {
    schmal = window.matchMedia(SCHMAL_MEDIA).matches;
  } catch {
    /* ohne matchMedia (Test-Umgebung): wie Desktop */
  }
  return standardOffen(gespeichert, schmal);
};

/** „Heat-Up = Einladungsmail …" → Wort fett, Erklärung normal. */
const Satz: FC<{ text: string }> = ({ text }) => {
  const i = text.indexOf(' = ');
  return i < 0 ? (
    <>{text}</>
  ) : (
    <>
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {text.slice(0, i)}
      </Box>
      {text.slice(i)}
    </>
  );
};

const Woerterzeile: FC<{ kapitel: WoerterKapitel }> = ({ kapitel }) => {
  const speicher = woerterSpeicher(kapitel);
  const saetze = woerterSaetze(kapitel);
  const [offen, setOffen] = useState(() => liesOffen(speicher));
  const umschalten = () => {
    setOffen((o) => {
      try {
        window.localStorage.setItem(speicher, o ? 'zu' : 'offen');
      } catch {
        /* nur Komfort */
      }
      return !o;
    });
  };
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 2, py: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Wörter auf dieser Seite
        </Typography>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={umschalten}
          aria-expanded={offen}
          sx={{ fontSize: 13 }}
        >
          {offen ? 'einklappen' : `${saetze.length} Wörter erklären`}
        </Link>
      </Box>
      <Collapse in={offen}>
        <Box
          component="ul"
          sx={{
            m: 0,
            mt: 0.75,
            p: 0,
            listStyle: 'none',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
              xl: 'repeat(3, minmax(0, 1fr))',
            },
            columnGap: 3,
            rowGap: 0.5,
          }}
        >
          {saetze.map((s) => (
            <Typography
              key={s}
              component="li"
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: 13, lineHeight: 1.5 }}
            >
              <Satz text={s} />
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default Woerterzeile;
